export type AnnotationKind = 'highlight' | 'note';
export type AnnotationVisibility = 'private';
export type AnnotationFilter = 'all' | 'highlights' | 'notes';
export type AnnotationColor =
  | 'yellow'
  | 'green'
  | 'blue'
  | 'pink'
  | 'purple'
  | 'orange';
export type AnnotationTextColor = 'black' | 'white' | 'gray' | 'blue' | 'red';

export const ANNOTATION_COLOR_VALUES: Record<AnnotationColor, string> = {
  blue: 'rgba(129, 212, 250, 0.4)',
  green: 'rgba(174, 213, 129, 0.4)',
  orange: 'rgba(255, 183, 77, 0.45)',
  pink: 'rgba(244, 143, 177, 0.4)',
  purple: 'rgba(179, 157, 219, 0.4)',
  yellow: 'rgba(255, 224, 102, 0.45)',
};

export const DEFAULT_ANNOTATION_COLOR: AnnotationColor = 'yellow';
export const DEFAULT_ANNOTATION_TEXT_COLOR: AnnotationTextColor = 'black';

export const ANNOTATION_TEXT_COLOR_VALUES: Record<AnnotationTextColor, string> = {
  black: 'var(--color-text-primary)',
  blue: 'var(--color-accent)',
  gray: 'var(--color-text-secondary)',
  red: 'var(--color-danger-text)',
  white: 'var(--color-bg-primary)',
};

export interface AnnotationQuote {
  endOffset: number | null;
  exact: string;
  prefix: string;
  startOffset: number | null;
  suffix: string;
}

export interface Annotation {
  contentUnitId: string;
  id: string;
  kind: AnnotationKind;
  note: string;
  quote: AnnotationQuote;
  textColor?: AnnotationTextColor;
  timestamp: number;
  visibility: AnnotationVisibility;
}

export interface NormalizedContentUnitAnnotationGroup {
  annotations: Annotation[];
  contentUnitId: string;
}

export type AnnotationResolution =
  | {
      confidence: 'high' | 'medium' | 'low';
      endOffset: number;
      startOffset: number;
      status: 'resolved';
      strategy: 'context' | 'exact' | 'offset';
    }
  | {
      confidence: 'none';
      status: 'orphaned';
      strategy: 'orphaned';
    };

export interface AnnotationRect {
  height: number;
  left: number;
  top: number;
  width: number;
}

export interface AnnotationReference {
  annotationId: string;
  contentUnitId: string;
}

export interface AnnotationSidebarItem {
  annotation: Annotation;
  isOrphaned: boolean;
  label: string;
}

export interface AnnotationSidebarGroup {
  id: string;
  items: AnnotationSidebarItem[];
  label: string;
}

export interface AnnotationDraft {
  contentUnitId: string;
  id: string;
  note: string;
  quote: AnnotationQuote;
  textColor?: AnnotationTextColor;
  timestamp: number;
}

export interface ActiveAnnotationSelection {
  contentUnitId: string;
  draft: AnnotationDraft;
  rect: AnnotationRect;
  selectedText: string;
}

export function getAnnotationKind(note: string): AnnotationKind {
  return note.trim().length > 0 ? 'note' : 'highlight';
}

export function getAnnotationDisplayLabel(annotation: Annotation) {
  return annotation.kind === 'note' ? 'Note' : 'Highlight';
}

export function createAnnotationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `annotation-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createAnnotationFromDraft(draft: AnnotationDraft): Annotation {
  return {
    contentUnitId: draft.contentUnitId,
    id: draft.id,
    kind: getAnnotationKind(draft.note),
    note: draft.note,
    quote: draft.quote,
    textColor: draft.textColor,
    timestamp: draft.timestamp,
    visibility: 'private',
  };
}

export function getAnnotationContainerId(contentUnitId: string) {
  return `annotation-content-unit-${contentUnitId}`;
}

export function getAnnotationHighlightId(annotation: AnnotationReference) {
  return `annotation-highlight-${annotation.contentUnitId}-${annotation.annotationId}`;
}

export function getAnnotationMarkerId(annotation: AnnotationReference) {
  return `annotation-marker-${annotation.contentUnitId}-${annotation.annotationId}`;
}

export function isAnnotationColor(value: unknown): value is AnnotationColor {
  return typeof value === 'string' && value in ANNOTATION_COLOR_VALUES;
}

export function isAnnotationTextColor(value: unknown): value is AnnotationTextColor {
  return typeof value === 'string' && value in ANNOTATION_TEXT_COLOR_VALUES;
}
